import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { FilterItem } from 'infra/FiltersViewModel'
import { JobsLoadFilterItemsPorts } from 'jobs/views/JobManagement/ports'
import { PublishedLegend } from 'legends/PublishedLegend'
import { FindLegends } from 'legends/PublishedLegendStore'
import orderBy from 'lodash/orderBy'
import {
  CursorPaged,
  CursorPageQuery,
  EncodedCursor,
  Id,
} from 'types/definitions'
import { FindUserTeams } from '../../iam/TeamStore'
import { FindUserTeamsQuery } from '../../iam/dtos/team'
import { SearchInventoryItems } from 'admin/InventoryStore'
import { FetchDocumentTypes } from 'documents/DocumentTypeStore'

export class JobsLoadFilterItemsAdapter implements JobsLoadFilterItemsPorts {
  constructor(
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private findLegends: FindLegends,
    private findUserTeams: FindUserTeams,
    private searchServiceItems: SearchInventoryItems,
    private _fetchDocumentTypes: FetchDocumentTypes,
  ) {}

  async fetchLegends(workspaceIds: Id[]): Promise<FilterItem<Id>[]> {
    const legends: PublishedLegend[] = []
    await Promise.all(
      workspaceIds.map(
        async (w) =>
          await this.findLegends({
            workspace_id: w ?? '',
            limit: 50,
          }),
      ),
    ).then((r) => r.map((r) => legends.push(...r.data)))

    return legends.map((l) => {
      return { title: l.name, description: l.workspace.name, value: l.id }
    })
  }

  getWorkspaces(): FilterItem<Id>[] {
    const memberships = this.getWorkspaceMemberships()
    return orderBy(
      memberships.map((m) => {
        return { title: m.workspace.name, value: m.workspace.id }
      }),
      (ws) => ws.title.toLowerCase(),
      'asc',
    )
  }

  async fetchTeams(
    query: FindUserTeamsQuery,
  ): Promise<CursorPaged<FilterItem<Id>>> {
    return this.findUserTeams(query).then((teams) => ({
      ...teams,
      data: teams.data.map((t) => ({
        title: t.name,
        description: t.workspace.name,
        value: t.id,
      })),
    }))
  }

  async fetchServiceItems(
    workspaceIds: Id[],
    paging: CursorPageQuery,
    searchText?: string,
  ): Promise<CursorPaged<FilterItem<Id>>> {
    const serviceItems = await this.searchServiceItems(
      workspaceIds,
      paging.limit || 20,
      searchText,
      paging.after?.toString(),
    )

    return {
      ...serviceItems,
      cursor: serviceItems.cursor as EncodedCursor,
      data: serviceItems.data.map((s) => {
        const description = s.global ? 'Global item' : s.workspace?.name
        return {
          title: s.name,
          description: !s.archived ? description : `${description} (archived)`,
          value: s.id,
        }
      }),
    }
  }

  async fetchDocumentTypes(): Promise<FilterItem<Id>[]> {
    const documentTypes = await this._fetchDocumentTypes()

    return documentTypes.map((dt) => {
      return {
        title: dt.name,
        value: dt.shortId,
      }
    })
  }
}
